/**
 * UploadDocumentDialog — drag-and-drop or URL upload for PDF/DOCX/PPTX/TXT.
 */
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, FileText, FileUp, X } from "lucide-react";

export default function UploadDocumentDialog({
  t, open, onOpenChange,
  uploadMode, setUploadMode,
  uploadFile, setUploadFile,
  fileUrl, setFileUrl,
  uploading, uploadProgress,
  onFileSelect, onUpload,
}) {
  const fileInputRef = useRef(null);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-green-500" />
            {t("upload_document")}
          </DialogTitle>
          <DialogDescription>{t("convert_to_slides_desc") || "Convert PDF, DOCX, PPTX, or TXT to slides"}</DialogDescription>
        </DialogHeader>
        <Tabs value={uploadMode} onValueChange={setUploadMode}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="file">{t("upload_file")}</TabsTrigger>
            <TabsTrigger value="url">{t("from_url") || "From URL"}</TabsTrigger>
          </TabsList>
          <TabsContent value="file" className="space-y-4 pt-4">
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-8 h-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium">{uploadFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(uploadFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <FileUp className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                  <p className="font-medium">{t("click_to_upload") || "Click to upload"}</p>
                  <p className="text-xs text-muted-foreground">PDF, DOCX, PPTX, TXT (Max 50MB)</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.docx,.pptx,.txt"
              onChange={onFileSelect}
            />
          </TabsContent>
          <TabsContent value="url" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>{t("document_url") || "Document URL"}</Label>
              <Input
                placeholder="https://example.com/document.pdf"
                value={fileUrl}
                onChange={(e) => setFileUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t("supports_drive_dropbox") || "Supports Google Drive, Dropbox, and direct links"}</p>
            </div>
          </TabsContent>
        </Tabs>
        {uploading && (
          <div className="space-y-2">
            <Progress value={uploadProgress} />
            <p className="text-xs text-center text-muted-foreground">{uploadProgress}% {t("uploaded") || "uploaded"}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
          <Button onClick={onUpload} disabled={uploading}>
            {uploading ? (t("converting") || "Converting...") : (t("convert_to_slides") || "Convert to Slides")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
